import { FinancialDocumentTypesEnum } from "@/shared/models/enums.type";
import { FinDocLineItemViewModel, FinDocViewModel } from "./models";
import { AgencyWorkModelsEnum } from "@/shared/models/agencyWorkModel";
import { round } from "@/components/numbers/numberFormatUtils";
import { TaxRateView } from "@/shared/models/taxRates";
import store from "@/store";
import { FinancialSettingsType } from "@/store/modules/financial";
import { AppParamsEnum } from "@/shared/models/appParam";
import * as _ from "lodash";

export function clearTaxExemption(lineItems: FinDocLineItemViewModel[], taxExemptionReason: string) {
    const appParams = store.state.appParams.appParams;
    const cityTaxExemption = appParams.find((x: { id: AppParamsEnum; }) => x.id === AppParamsEnum.CityTaxExemptionReason)?.value?.toString() ?? "";
    const stayNotPayingTaxExemption = appParams.find((x: { id: AppParamsEnum; }) => x.id === AppParamsEnum.StayLineItemsExemptionReason)?.value?.toString() ?? "";
    const tenantNotPayingTaxExemption = appParams.find((x: { id: AppParamsEnum; }) => x.id === AppParamsEnum.TenantDoesNotPayVatExemptionReason)?.value?.toString() ?? "";
    const exchangeRateDifferenceExemption = appParams.find((x: { id: AppParamsEnum; }) => x.id === AppParamsEnum.ExchangeRateDifferenceExemptionReason)?.value?.toString() ?? "";

    let userInput = taxExemptionReason != undefined
        ? taxExemptionReason
            .replace(cityTaxExemption, "")
            .replace(stayNotPayingTaxExemption, "")
            .replace(tenantNotPayingTaxExemption, "")
            .replace(exchangeRateDifferenceExemption, "")
            .trim()
        : "";

    for (const item of lineItems) {
        if (item.taxExemptionReasonFromAOS) {
            userInput = userInput.replace(item.taxExemptionReasonFromAOS, "").trim();
        }
    }
    return userInput;
}

export function fillInvoiceEditingTaxExemptionReason(finDocModel: FinDocViewModel, isForCancellation = false, tenantPaysVat = true) {
    const items = finDocModel.lineItems;

    const financialSettings: FinancialSettingsType = store.state.financial.model;
    //TODO: when we decide on how to handle tax exemptions for different countries, this has to be changed accordingly
    const stayVATRatesForExemption = financialSettings.taxRates.filter(e => e.isOutOfScope).map(e => e.id);
    const AOSTaxRatesForExemption = financialSettings.taxRates.filter(e => e.isTaxExempt || e.isOutOfScope).map(e => e.id);

    const remarks = [];

    const appParams = store.state.appParams.appParams;
    const stayVatReverseChargeExempt = appParams.find((x: { id: AppParamsEnum; }) => x.id === AppParamsEnum.StayLineItemsReverseChargeExemptionReason)?.value?.toString() ?? "";
    const tenantVatExemption = appParams.find((x: { id: AppParamsEnum; }) => x.id === AppParamsEnum.TenantDoesNotPayVatExemptionReason)?.value?.toString() ?? "";
    const stayVatExemption = appParams.find((x: { id: AppParamsEnum; }) => x.id === AppParamsEnum.StayLineItemsExemptionReason)?.value?.toString() ?? "";
    const cancellationExemption = appParams.find((x: { id: AppParamsEnum; }) => x.id === AppParamsEnum.CancellationExemptionReason)?.value?.toString() ?? "";
    const exchangeRateDifferenceExemption = appParams.find((x: { id: AppParamsEnum; }) => x.id === AppParamsEnum.ExchangeRateDifferenceExemptionReason)?.value?.toString() ?? "";

    if (finDocModel.documentTypeId === FinancialDocumentTypesEnum.VlasnikovRacun) {
        if (items.some(i => !i.ownerIsResident && !i.ownerVatId)) {
            remarks.push(stayVatReverseChargeExempt);
        }
        else if (items.some(i => !i.ownerPaysVAT)) {
            remarks.push(tenantVatExemption);
        }
    }
    else {
        // VAT exemption reason in case tenant/owner doesn't pay VAT
        if (items.some(i => ((i.agencyWorkModelId == AgencyWorkModelsEnum.OwnNameAndOwnInvoice || i.agencyWorkModelId === null || i.agencyWorkModelId === undefined) && !tenantPaysVat)
            || (i.agencyWorkModelId == AgencyWorkModelsEnum.OtherNameAndOtherInvoice && !i.ownerPaysVAT && finDocModel.basicInfo.showOwnerDetails)
            || (i.agencyWorkModelId == AgencyWorkModelsEnum.OtherNameAndOtherInvoice && !tenantPaysVat && !finDocModel.basicInfo.showOwnerDetails))) {
            remarks.push(tenantVatExemption);
        }

        // stay VAT exemption reason in case a stay item doesn't have a tax rate set
        if ((items.some(i => i.agencyWorkModelId == AgencyWorkModelsEnum.OwnNameAndOtherInvoice && !i.addOnServiceId && i.taxRateId == financialSettings.outOfScopeTaxRate.id)) ||
            (items.some(i => i.agencyWorkModelId == AgencyWorkModelsEnum.OwnNameAndOtherInvoice && !i.addOnServiceId && i.reservationAccUnitTypeId && (stayVATRatesForExemption.includes(i.taxRateId))))
            && !isForCancellation) {
            remarks.push(stayVatExemption);
        }

        // cancelation VAT exemption reason in case 
        if ((items.some(i => i.agencyWorkModelId == AgencyWorkModelsEnum.OwnNameAndOtherInvoice) ||
            items.some(i => i.agencyWorkModelId == AgencyWorkModelsEnum.OwnNameAndOwnInvoice && tenantPaysVat) ||
            items.some(i => i.agencyWorkModelId == AgencyWorkModelsEnum.OtherNameAndOtherInvoice && i.ownerPaysVAT && finDocModel.basicInfo.showOwnerDetails) ||
            items.some(i => i.agencyWorkModelId == AgencyWorkModelsEnum.OtherNameAndOtherInvoice && tenantPaysVat && !finDocModel.basicInfo.showOwnerDetails)
        )
            && isForCancellation) {
            remarks.push(cancellationExemption);
        }

        // AOS tax exemption (including city tax)
        for (const item of items.filter((x) => AOSTaxRatesForExemption.includes(x.taxRateId))) {
            if (!remarks.includes(item.taxExemptionReasonFromAOS) && !!item.taxExemptionReasonFromAOS) {
                remarks.push(item.taxExemptionReasonFromAOS);
            }
        }
    }

    if (items.some(x => x.isExchangeRateDifference)) {
        remarks.push(exchangeRateDifferenceExemption);
    }

    finDocModel.taxExemptionReason = remarks.join("\n");
}

export function calculateLineItem(lineItem: FinDocLineItemViewModel, taxRates: TaxRateView[], workingToFormalConversionRate: number, financialDocument?: FinDocViewModel) {
    const taxRateFactors = {} as any;
    for (const rate of taxRates) {
        taxRateFactors[`${rate.id}`] = round(rate.rate / 100, 8);
    }

    let taxRateFactor = taxRateFactors[`${lineItem.taxRateId}`];
    if (taxRateFactor === undefined) taxRateFactor = 0;

    const consumptionTaxRateFactor = round((lineItem.consumptionTaxRate ?? 0) / 100, 8);
    const totalTaxRateFactor = round(taxRateFactor + consumptionTaxRateFactor, 8);
    const discountFactor = round((1 - lineItem.discountPercent / 100), 8);

    const totalGrossPrice = round(lineItem.grossUnitPrice * lineItem.decimalQuantity, 2);

    lineItem.grossPrice = round(totalGrossPrice * discountFactor, 2);
    lineItem.netPrice = round(lineItem.grossPrice / (1 + totalTaxRateFactor), 2);
    // using 1 as default quantity to avoid "divide by 0" errors
    // if quantity is 0 then all prices except gross unit price are 0
    lineItem.netUnitPrice = round(lineItem.netPrice / (discountFactor !== 0 ? discountFactor : 1), 2);
    lineItem.netUnitPrice = round(lineItem.netUnitPrice / (lineItem.decimalQuantity !== 0 ? lineItem.decimalQuantity : 1), 2);

    // correct line item net prices so their sum matches the document total
    // skip if there is no document (invoice routing) or any line item has not been calculated yet
    if (financialDocument && !financialDocument.lineItems.some(e => e.grossPrice === undefined || e.netPrice === undefined)) {
        const lineItemsPerTaxCategory = _.groupBy(financialDocument.lineItems, e => `${taxRateFactors[`${e.taxRateId}`]}|${e.consumptionTaxRate !== null && e.consumptionTaxRate !== undefined ? round((e.consumptionTaxRate ?? 0) / 100, 4) : null}`);
        for (const key in lineItemsPerTaxCategory) {
            const categoryLineItems = lineItemsPerTaxCategory[key];

            // undo any previous net price corrections
            categoryLineItems.forEach(e => {
                const df = round(e.discountPercent / 100, 8);
                e.netPrice = round(e.netPrice - (e.netPriceCorrectionAmount ?? 0), 2);
                e.netUnitPrice = round(e.netPrice / (1 - df), 2);
                e.netUnitPrice = round(e.netUnitPrice / (e.decimalQuantity !== 0 ? e.decimalQuantity : 1), 2);
                e.netPriceCorrectionAmount = 0;
            });

            const categoryTaxRateFactor = round(Number.parseFloat(key.split('|')[0]), 8);
            const consumptionTaxKeyPart = key.split('|')[1];
            const categoryConsumptionTaxRateFactor = consumptionTaxKeyPart !== 'null' ? round(Number.parseFloat(consumptionTaxKeyPart), 8) : 0;
            const categoryTotalTaxFactor = round(categoryTaxRateFactor + categoryConsumptionTaxRateFactor, 8);

            // find the net price difference
            const categoryGrossPriceSum = round(categoryLineItems.reduce((sum, e) => sum + e.grossPrice, 0), 2);
            const categoryNetPriceSum = round(categoryLineItems.reduce((sum, e) => sum + e.netPrice, 0), 2);

            const categoryActualNetPrice = round(categoryGrossPriceSum / (1 + categoryTotalTaxFactor), 2);
            let netPriceDiff = round(categoryActualNetPrice - categoryNetPriceSum, 2);

            // if there is a difference then apply it to items in increments of 0.01 from the highest gross price to the lowest
            if (netPriceDiff !== 0) {
                const sortedCategoryLineItems = categoryLineItems.sort((a, b) => b.grossPrice - a.grossPrice);
                let itemIndex = 0;
                while (netPriceDiff != 0) {
                    if (netPriceDiff > 0) {
                        sortedCategoryLineItems[itemIndex].netPriceCorrectionAmount = round(sortedCategoryLineItems[itemIndex].netPriceCorrectionAmount + 0.01, 2);
                        sortedCategoryLineItems[itemIndex].netPrice = round(sortedCategoryLineItems[itemIndex].netPrice + 0.01, 2);
                        netPriceDiff = round(netPriceDiff - 0.01, 2);
                    } else {
                        sortedCategoryLineItems[itemIndex].netPriceCorrectionAmount = round(sortedCategoryLineItems[itemIndex].netPriceCorrectionAmount - 0.01, 2);
                        sortedCategoryLineItems[itemIndex].netPrice = round(sortedCategoryLineItems[itemIndex].netPrice - 0.01, 2);
                        netPriceDiff = round(netPriceDiff + 0.01, 2);
                    }

                    const itemDiscountFactor = round(sortedCategoryLineItems[itemIndex].discountPercent / 100, 8);
                    sortedCategoryLineItems[itemIndex].netUnitPrice = round(sortedCategoryLineItems[itemIndex].netPrice / (1 - itemDiscountFactor), 2);
                    sortedCategoryLineItems[itemIndex].netUnitPrice = round(sortedCategoryLineItems[itemIndex].netUnitPrice / (sortedCategoryLineItems[itemIndex].decimalQuantity !== 0 ? sortedCategoryLineItems[itemIndex].decimalQuantity : 1), 2);

                    // go to next item index, if out of bounds then reset to 0
                    if (++itemIndex > (categoryLineItems.length - 1)) itemIndex = 0;
                }
            }

            // calculate alternative currency values
            categoryLineItems.forEach(e => calculateLineItemAltCurrency(e, workingToFormalConversionRate));
        }
    }
    else {
        // calculate alternative currency values
        calculateLineItemAltCurrency(lineItem, workingToFormalConversionRate);
    }
}

export function calculateLineItemDiscount(lineItem: FinDocLineItemViewModel, taxRates: TaxRateView[], workingToFormalConversionRate: number, financialDocument?: FinDocViewModel) {
    if (lineItem.grossUnitPrice) {
        const totalGrossPrice = round(lineItem.grossUnitPrice * lineItem.decimalQuantity, 2);
        const priceFactor = lineItem.grossPrice / totalGrossPrice;
        const discountFactor = (1 - priceFactor) * 100;
        lineItem.discountPercent = round(discountFactor, 6);
    }
    // if gross unit price is not set
    // then set it to total gross price / quantity, and reset the discount
    else {
        lineItem.grossUnitPrice = round(lineItem.grossPrice / (lineItem.decimalQuantity ? lineItem.decimalQuantity : 1));
        lineItem.discountPercent = 0;
    }

    // recalculate the item at the end to get everything right
    calculateLineItem(lineItem, taxRates, workingToFormalConversionRate, financialDocument);
}

function calculateLineItemAltCurrency(lineItem: FinDocLineItemViewModel, workingToFormalConversionRate: number) {
    lineItem.grossUnitPriceAlt = round(lineItem.grossUnitPrice * workingToFormalConversionRate, 2);
    lineItem.grossPriceAlt = round(lineItem.grossPrice * workingToFormalConversionRate, 2);
    lineItem.netUnitPriceAlt = round(lineItem.netUnitPrice * workingToFormalConversionRate, 2);
    lineItem.netPriceAlt = round(lineItem.netPrice * workingToFormalConversionRate, 2);
    lineItem.netPriceCorrectionAmountAlt = round(lineItem.netPriceCorrectionAmount * workingToFormalConversionRate, 2);
}

export function getPriceSummary(document: FinDocViewModel) {
    const itemsCopy = _.cloneDeep(document.lineItems);
    const advanceCopy = _.cloneDeep(
        document.relatedAdvanceInvoices
    );
    const gross =
        itemsCopy.length === 0
            ? 0
            : round(
                itemsCopy.reduce((sum, e) => sum + e.grossPrice, 0),
                2
            );
    const net =
        itemsCopy.length === 0
            ? 0
            : round(
                itemsCopy.reduce((sum, e) => sum + e.netPrice, 0),
                2
            );
    const advance =
        advanceCopy.length === 0
            ? 0
            : round(
                advanceCopy.reduce(
                    (sum, e) =>
                        sum +
                        e.totalDueAmount *
                        (document.documentTypeId ===
                            FinancialDocumentTypesEnum.Storno
                            ? 1
                            : -1),
                    0
                ),
                2
            );

    const discount = -round(
        (gross * document.discountPercent) / 100,
        2
    );
    const toPay = round(gross + advance - discount, 2);
    const toPayWithoutAdvance = round(gross - discount, 2);

    return {
        gross: gross,
        net: net,
        advance: advance,
        discount: discount,
        toPay: toPay,
        toPayWithoutAdvance: toPayWithoutAdvance
    };
}